<template>
  <Layout>
    <template #header-container>
      <div class="mc-list-header mb-2">
        <PageHeader isSticky :title="title">
          <template #afterTitle>
            <div class="d-flex gap-2">
              <template v-if="status !== null">
                <McButton
                  variant="secondary"
                  icon="icon-outline_arrow-left"
                  :text="$t('global.back')"
                  :disabled="isLoading"
                  @click="$router.push({ name: 'Sales Order' })"
                />

                <div class="__divider" />

                <McButton
                  variant="secondary"
                  icon="icon-outline_close"
                  :text="$t('global.action.cancel')"
                  :disabled="isLoading || !hasChanged"
                  @click="handleCancelEdit"
                />

                <McButton
                  icon="icon-outline_save"
                  :text="$t('global.save')"
                  :disabled="isLoading || !hasChanged || hasError"
                  @click="handleSave"
                />

                <div class="__divider" />

                <McButton
                  variant="secondary"
                  icon="icon-outline_history"
                  :text="$t('salesOrder.text.history')"
                  :disabled="isLoading"
                  @click="handleGetHistory"
                />

                <TransitionStatusBtn
                  :currentStatus="status"
                  :statuses="transitionStatusBtnItems"
                  :isLoading="isLoading"
                  @click="handleTransitionStatus"
                />
              </template>

              <template v-else>
                <McButton
                  variant="secondary"
                  icon="icon-outline_close"
                  :disabled="isLoading"
                  :text="$t('global.action.cancel')"
                  @click="handleCancelStore"
                />
                <SaveButton
                  :statuses="saveBtnItems"
                  :isLoading="isLoading || !hasChanged"
                  @click="handleSave"
                />
              </template>
            </div>
          </template>

          <template v-if="status" #footer>
            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <b-skeleton-img no-aspect height="30px" class="mc-wizard-skeleton mt-3" />
              </template>
              <McWizard
                compact
                isSingleStatus
                :items="wizardItems"
                :activeKey="status"
                class="mc-wizard mt-3"
              />
            </b-skeleton-wrapper>
          </template>
        </PageHeader>
      </div>
    </template>

    <FormulateForm v-model="formValues" :errors="formErrors">
      <b-card>
        <h4 class="card-title flex-grow-1">{{ $t('salesOrder.text.basicInfo') }}</h4>
        <b-row cols="4">
          <b-col v-for="field in basicInfoFields" :key="field.name">
            <SuggestionSelect
              v-if="field.type === 'taggable-select-input'"
              v-model="formValues[field.name]"
              :field="field"
              :disabled="isLoading"
              @input="handleInput(field.name)"
            />
            <FormulateInput v-else v-bind="field" :disabled="isLoading">
              <template v-if="field.prefix" #prefix>
                <div class="formulate-input-prefix">
                  <span>{{ field.prefix }}</span>
                </div>
              </template>
              <template v-if="field.suffix" #suffix>
                <div class="formulate-input-suffix">
                  <span>{{ field.suffix }}</span>
                </div>
              </template>
            </FormulateInput>
          </b-col>
        </b-row>
        <hr />

        <h4 class="card-title mt-2 flex-grow-1">
          {{ $t('salesOrder.text.deliveryContact') }}
        </h4>
        <b-row cols="4" class="mt-2">
          <b-col v-for="field in customerInfoFields" :key="field.name">
            <SuggestionSelect
              v-model="formValues[field.name]"
              :field="field"
              :customerName="formValues.customer_name"
              :disabled="isLoading"
              @input="handleInput(field.name)"
            />
          </b-col>
        </b-row>
        <hr />

        <h4 class="card-title mt-2 flex-grow-1">
          {{ $t('salesOrder.text.otherInfo') }}
        </h4>
        <b-row cols="4">
          <b-col>
            <FormulateInput v-bind="otherInfoField" :disabled="isLoading" />
          </b-col>
        </b-row>
      </b-card>

      <b-card>
        <h4 class="card-title mb-2 flex-grow-1">
          {{ $t('salesOrder.text.deliveryLocations') }}
        </h4>

        <b-row>
          <b-col cols="12" md="6" class="d-flex flex-column gap-2">
            <OrderableTextListInput
              ref="orderable-locations-input"
              v-model="formValues.locations"
              :field="locationField"
            />
            <McButton
              block
              icon="icon-outline_plus"
              :text="$t('salesOrder.text.addLocation')"
              class="mt-2"
              :disabled="isLoading"
              @click="handleAddLocation"
            />
          </b-col>
        </b-row>
      </b-card>
    </FormulateForm>

    <b-card v-if="status !== null">
      <McTable
        :key="ftiTableKey"
        :apiClient="fleetTaskInstantApiClient"
        :fields="fleetTaskInstantFields"
        :hasDelete="false"
        :hasCheckbox="false"
        :searchable="true"
        :customHandlers="customTableHandlers"
        :initialPage="initialParams.page"
        :initialLimit="initialParams.limit"
        :initialSearch="initialParams.search"
        @listRes="handleSuccessGetFleetTaskInstant"
      >
        <template #customDisplayItems>
          <h4 class="card-title mt-2 flex-grow-1">
            {{ $t('salesOrder.text.quickAssignmentList') }}
          </h4>
        </template>

        <!-- status -->
        <template #[`cell(status.name)`]="{ item, value }">
          <span
            v-if="item.status?.color"
            class="__status-dots"
            :style="{ backgroundColor: item.status.color }"
          ></span>
          <span class="mc-text-status">{{ value }}</span>
        </template>

        <template #[`cell(driver.name)`]="{ value }">
          {{ value || '-' }}
        </template>

        <!-- Created On -->
        <template #cell(created_on)="{ value }">
          <template v-if="value">
            <div>{{ formatIndexDate(value).date }}</div>
            <div class="mc-datetime">
              {{ formatIndexDate(value).time }}
            </div>
          </template>
          <template v-else> - </template>
        </template>

        <!-- Track FO -->
        <template #cell(track_fo)="{ item }">
          <ShareTrackButton :rawStatus="item.status.raw_type" @click="shareTrackBtnHandler(item)" />
        </template>

        <!-- Driver Document -->
        <template #cell(driver_attachment)="{ item }">
          <McButton
            variant="secondary"
            size="sm"
            :text="$t('salesOrder.text.seeDocument')"
            @click="openDriverDocumentModal(item)"
          />
        </template>
      </McTable>

      <ShareTrackModal
        modalName="share-track-modal"
        :number="selectedFOData?.number"
        :trackLink="selectedFOData?.track_link"
      />
    </b-card>

    <McEventList
      :value.sync="openHistory"
      :data="historyData"
      :keys="historyKeys"
      :title="$t('salesOrder.text.statusHistory')"
    />

    <TransitionToCancelModal
      ref="mc-modal-canceled"
      :entityData="showResponse"
      :newStatusTransition="{ status: { name: status } }"
      :selectedItems="[showResponse]"
      @transition="handleTransitionStatus('CANCELED', true, $event)"
    />

    <McConfirmation
      v-model="isShowDeleteModal"
      centered
      noCloseOnEsc
      noCloseOnBackdrop
      :title="$t('salesOrder.modal.delete.title')"
      :ok-title="$t('global.delete')"
      :cancel-title="$t('global.cancel')"
      @handle-ok="handleTransitionStatus('DELETE', true)"
      @cancel="isShowDeleteModal = false"
    >
      <p v-html="$t('salesOrder.modal.delete.areYouSure', { soNumber: showResponse.number })" />
    </McConfirmation>

    <ModalDriverDocument
      :foId="selectedFOData?.id"
      :attachments="selectedFOData?.attachment"
      @refetch-list-fo="handleRefetchFleetTaskInstant"
    />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapGetters } from 'vuex'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McWizard from '@/components/McWizard'
import McTable from '@/components/McTable'
import McEventList from '@/components/McEventList'
import McConfirmation from '@/components/McConfirmation'

import SaveButton from './components/SaveButton.vue'
import TransitionStatusBtn from './components/TransitionStatusBtn.vue'
import SuggestionSelect from './components/SuggestionSelect.vue'
import OrderableTextListInput from './components/OrderableTextListInput.vue'
import TransitionToCancelModal from './components/TransitionToCancelModal.vue'
import ModalDriverDocument from './components/ModalDriverDocument.vue'

import statuses, { saveBtnItems, transitionStatusItems } from './lib/constants/status.js'
import { successMsg, errorMsg } from './lib/constants/toast-msg'

import * as SalesOrder from '@/services/rest-api/lib/sales-order/sales-order.js'

import { get, isEqual, cloneDeep } from 'lodash'
import { formatIndexDate } from '@/helpers/tzDate'

import formFields from './lib/fields/form'
import fleetTaskInstantFields from './lib/fields/fleet-task-instant'

import errorMappings from './lib/constants/error-mappings'

import ShareTrackModal from '@/router/views/fleet-task-instant/components/ShareTrackModal'
import ShareTrackButton from '@/router/views/fleet-task-instant/components/ShareTrackButton'

export default {
  page: {
    title: 'Sales Order',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McWizard,
    McTable,
    McEventList,
    McConfirmation,
    SaveButton,
    TransitionStatusBtn,
    SuggestionSelect,
    OrderableTextListInput,
    TransitionToCancelModal,
    ModalDriverDocument,
    ShareTrackModal,
    ShareTrackButton,
  },
  mixins: [formFields, fleetTaskInstantFields],
  data() {
    return {
      isLoading: false,
      isAdding: false,
      openHistory: false,
      isShowDeleteModal: false,
      locationInputHasError: false,

      historyData: [],
      historyKeys: [],

      showResponse: {},
      formValues: {
        locations: [],
      },
      oldFormValues: {
        locations: [],
      },

      errorCodes: {},
      formErrors: {},

      ftiTableKey: 1,
      selectedFOData: null,
      initialParams: {},
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    title() {
      return this.$route.params.id
        ? `${this.$t('salesOrder.title.detail')} #${this.showResponse?.number}`
        : this.$t('salesOrder.title.create')
    },
    id() {
      return this.$route.params.id || null
    },
    status() {
      return this.showResponse?.status?.raw_type || null
    },
    statusItems() {
      return statuses(this)
    },
    saveBtnItems() {
      return saveBtnItems.map((status) => {
        return {
          key: status,
          ...this.statusItems[status],
        }
      })
    },
    transitionStatusBtnItems() {
      return transitionStatusItems[this.status].map((status) => {
        return {
          key: status,
          ...this.statusItems[status],
        }
      })
    },
    wizardItems() {
      if (this.status === 'CANCELED') {
        return [
          this.statusItems.DRAFT,
          this.statusItems.CONFIRMED,
          this.statusItems.ASSIGNED,
          this.statusItems.CANCELED,
        ]
      }

      return [
        this.statusItems.DRAFT,
        this.statusItems.CONFIRMED,
        this.statusItems.ASSIGNED,
        this.statusItems.COMPLETED,
      ]
    },
    fields() {
      return [
        ...this.basicInfoFields,
        ...this.customerInfoFields,
        this.otherInfoField,
        this.locationField,
      ]
    },
    fieldKeys() {
      return this.fields.map((item) => item.key)
    },
    hasChanged() {
      const [_, hasChanged] = this.filterUnchangedData()
      return hasChanged
    },
    hasFleetTaskInstantScope() {
      return this.hasAccess([
        'fleet-task-instant:read',
        'fleet-task-instant:create',
        'fleet-task-instant:update',
        'fleet-task-instant:delete',
      ])
    },
    fleetTaskInstantApiClient() {
      let get = SalesOrder.getFleetTaskInstant(this.id, this.handleSetParams)

      return {
        get,
      }
    },
    customTableHandlers() {
      return {
        handleClickRow: this.handleClickRow,
      }
    },
    hasError() {
      return Object.keys(this.formErrors).length > 0 || this.locationInputHasError
    },
  },
  watch: {
    'formValues.locations': {
      async handler() {
        this.locationInputHasError = false
        for (const index in this.formValues.locations) {
          const hasValidationError = await this.$refs['orderable-locations-input']
            ?.Refs(index)
            ?.hasValidationErrors()
          this.locationInputHasError = this.locationInputHasError || hasValidationError
        }
      },
      deep: true,
    },
    '$i18n.locale': function () {
      if (this.errorCodes && Object.keys(this.errorCodes).length > 0) {
        for (const key in this.errorCodes) {
          this.$set(this.formErrors, key, this.$t(this.errorCodes[key]))
        }
      }
    },
  },
  mounted() {
    if (!this.id) {
      this.isAdding = true
      return
    }

    this.showData()
  },
  methods: {
    formatIndexDate,

    parseResponseData() {
      this.fields.forEach((field) => {
        let value = get(this.showResponse, field.responseKey)

        if (Array.isArray(value)) {
          this.$set(this.formValues, field.key, cloneDeep(value))
          this.$set(this.oldFormValues, field.key, cloneDeep(value))
          return
        }

        this.$set(this.formValues, field.key, value)
        this.$set(this.oldFormValues, field.key, value)
      })
    },

    async showData() {
      this.isLoading = true
      const res = await SalesOrder.show(this.id)

      if (res.errors) return

      this.showResponse = res.data
      this.parseResponseData()

      this.isLoading = false
    },

    async handleGetHistory() {
      this.openHistory = true
      await this.fetchHistoryListData()
    },

    async fetchHistoryListData() {
      const res = await SalesOrder.getHistory(this.id, { type: 'status' })

      let historyData = res.data
      historyData = historyData.map((data) => {
        const date = new Date(data.created_on)

        const options = { day: '2-digit', month: 'short', year: 'numeric' }
        const formattedDate = date.toLocaleDateString('id-ID', options)

        data.date = formattedDate
        return data
      })

      historyData = Object.groupBy(historyData, ({ date }) => date)

      this.historyKeys = Object.keys(historyData)

      historyData = Object.keys(historyData).map((date) => ({
        [date]: historyData[date],
      }))

      this.historyData = historyData
    },

    handleAddLocation() {
      const locations = [...this.formValues.locations, null]
      this.$set(this.formValues, 'locations', locations)
    },

    filterUnchangedData() {
      const newFormValues = Object.assign(
        {},
        ...this.fields.map((field) => {
          const key = field.key
          const value = this.formValues[key]

          if (field && field._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0

            return { [key]: isValid ? Number(value) : null }
          }

          return { [key]: value }
        })
      )

      const keys = new Set([...Object.keys(newFormValues), ...Object.keys(this.oldFormValues)])
      const _newData = {}

      keys.forEach((key) => {
        if (this.isAdding && (!newFormValues[key] || newFormValues[key] === '')) return
        if (this.oldFormValues[key] == newFormValues[key]) return

        let newValues = newFormValues[key]
        const oldValues = this.oldFormValues[key]

        if (key === 'locations' && oldValues.length === newValues.length) {
          if (isEqual(oldValues, newValues)) return

          newValues = newValues.filter((item) => item !== null)
        }

        _newData[key] = newValues
      })

      const entries = Object.entries(_newData)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newData, hasChanged]
    },

    handleCancelStore() {
      this.$router.push({ name: 'Sales Order' })
    },

    handleCancelEdit() {
      this.parseResponseData()
    },

    async submitStore(data) {
      const res = await SalesOrder.store(data)

      if (res.errors_v2 || res.errors) {
        this.handleFormErrors(res.errors_v2?.issues)
        this.isLoading = false
        return
      }

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              tag: 'strong',
              text: `${res.data?.number}`,
            },
            { text: ' berhasil disimpan.' },
          ],
        },
      })

      this.$router.push({ name: 'Sales Order' })
    },

    async submitUpdate(data) {
      const res = await SalesOrder.update(this.id, data)

      if (res.errors_v2 || res.errors) {
        this.handleFormErrors(res.errors_v2?.issues)
        this.isLoading = false
        return
      }

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              tag: 'strong',
              text: `${this.showResponse?.number}`,
            },
            { text: ' berhasil disimpan.' },
          ],
        },
      })

      await this.showData()
    },

    async handleSave(status) {
      this.isLoading = true

      const [data, hasChanged] = this.filterUnchangedData()

      if (!hasChanged) {
        this.isLoading = false
        return
      }

      if (this.isAdding) {
        data.status = status

        await this.submitStore(data)
      } else {
        await this.submitUpdate(data)
      }
    },

    async handleTransitionStatus(status, isConfirmAction = false, payload = {}) {
      if (status === 'DELETE') {
        if (!isConfirmAction) {
          this.isShowDeleteModal = true
          return
        }

        this.isShowDeleteModal = false
        this.isLoading = true

        const res = await SalesOrder.deleteSingle(this.id)

        if (res.errors) {
          const errorsV2 = res.errors_v2

          let message = errorMsg['delete-so/detail']()
          if (
            errorsV2?.issues?.[0].code ===
            'DELIVERY_ORDER.SALES_ORDER_ASSIGNED_TO_FLEET_TASK_INSTANT'
          ) {
            message = errorMsg['delete-so-assigned/detail'](this.showResponse.number)
          }

          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: message,
                },
              ],
            },
          })
          this.isLoading = false

          return
        }

        this.$mcToast.success(successMsg['delete-so/detail']())
        this.$router.push({ name: 'Sales Order' })
        return
      } else if (status === 'CANCELED' && !isConfirmAction) {
        this.$refs['mc-modal-canceled'].show()
        this.isLoading = false
        return
      }

      this.isLoading = true

      const data = {
        status,
      }

      if (status === 'CANCELED' && isConfirmAction) {
        data.fields = payload.additionalPayload.fields
      }

      const res = await SalesOrder.transition(this.id, data)

      if (status === 'CANCELED') {
        this.$refs['mc-modal-canceled'].hide()
      }

      if (res.errors) {
        this.$mcToast.error(
          errorMsg[`transition-${status.toLowerCase()}`](this.showResponse.number)
        )
        this.isLoading = false
        return
      }

      this.$mcToast.success(
        successMsg[`transition-${status.toLowerCase()}`](this.showResponse.number)
      )

      await this.showData()
    },

    handleSetParams(params) {
      this.$set(this.initialParams, 'page', params.page)
      this.$set(this.initialParams, 'limit', params.limit)
      this.$set(this.initialParams, 'search', params.search)
    },

    handleOpenNewTab(item) {
      if (!this.hasFleetTaskInstantScope) {
        this.shareTrackBtnHandler(item)
        return
      }

      return this.$router.resolve({
        name: 'Fleet Task Instant Detail',
        params: { id: item.id },
      }).href
    },

    handleClickRow(item) {
      if (this.hasFleetTaskInstantScope) {
        const route = this.$router.resolve({ name: 'Fleet Task Instant Detail', params: { id: item.id } })
        window.open(route.href, '_blank')

        return
      }

      if (!['STARTED', 'ENDED'].includes(item.status.raw_type)) return

      const url = item.track_link
      window.open(url, '_blank')
    },

    shareTrackBtnHandler(item) {
      this.selectedFOData = item
      this.$bvModal.show('share-track-modal')
    },

    handleInput(fieldName) {
      this.$delete(this.errorCodes, fieldName)
      this.$delete(this.formErrors, fieldName)
    },

    handleFormErrors(errors) {
      errors.map((issue) => {
        const key = issue.path.join('.')
        const errorCode = this.getErrorCode(issue.message)

        if (!errorCode) {
          this.$mcToast.error(this.$t('global.somethingWentWrong'))
          this.$set(this.formErrors, key, ' ')
          return
        }

        this.$set(this.errorCodes, key, errorCode)
        this.$set(this.formErrors, key, this.$t(errorCode))
      })
    },

    getErrorCode(message) {
      for (const { match, code } of errorMappings) {
        if (match instanceof RegExp && match.test(message)) {
          return code
        }
      }

      return undefined
    },

    openDriverDocumentModal(item) {
      this.selectedFOData = item
      this.$bvModal.show('modal-driver-document')
    },

    handleRefetchFleetTaskInstant() {
      this.ftiTableKey += 1
    },

    handleSuccessGetFleetTaskInstant(res) {
      if (res.errors || !this.selectedFOData?.id) return

      const newItem = res.data.paginated_result.find(item => item.id === this.selectedFOData.id)

      if (!newItem) return

      this.selectedFOData = newItem
    },
  },
}
</script>

<style lang="scss" scoped>
.__divider {
  margin: 0 12px;
  width: 1px;
  min-height: 37.6px;
  height: 100%;
  background-color: $teal-100;
}

.__status-dots {
  display: inline-block;
  margin: auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 8px;
}
</style>
